import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, Copy, RefreshCw, Check, MessageSquare, Bookmark, X, Loader2, Sparkles, Zap, Lock } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../lib/auth';
import redditPixel from '../lib/tracking';
import api from '../lib/api';
import { toast } from 'sonner';

function cleanPostBody(text) {
  if (!text) return '';
  return text.replace(/submitted by\/?u\/\S+\[link\]\[comments\]/gi, '').replace(/submitted by\/?u\/\S+/gi, '').trimEnd();
}

function IntentBadge({ score }) {
  let cls = 'intent-low';
  let ring = 'ring-gray-200';
  if (score >= 70) { cls = 'intent-high'; ring = 'ring-orange-200'; }
  else if (score >= 40) { cls = 'intent-medium'; ring = 'ring-amber-200'; }
  return (
    <span className={`${cls} text-sm font-bold px-3 py-1.5 rounded-full inline-block tabular-nums ring-1 ${ring}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      {score}
    </span>
  );
}

function StatusBadge({ status }) {
  const cls = `status-${status}`;
  return <Badge variant="outline" className={`text-xs border ${cls} capitalize font-medium`}>{status}</Badge>;
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.4, ease: [0.22, 1, 0.36, 1] } })
};

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isFreePlan = (user?.plan || 'free') === 'free' && user?.role !== 'admin';
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);
  const [tone, setTone] = useState('helpful');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchLead = async () => {
      try {
        const data = await api.getLead(leadId);
        setLead(data);
        redditPixel.leadViewed(leadId, data.intent_score, data.subreddit);
      } catch (err) {
        toast.error('Failed to load lead');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchLead();
  }, [leadId, navigate]);

  const handleGenerateReply = async () => {
    setReplyLoading(true);
    try {
      const data = await api.generateReply(leadId, tone);
      setReply(data.reply || data);
      toast.success('Reply generated!');
      redditPixel.replyGenerated(leadId, tone);
    } catch (err) {
      toast.error(err.message || 'Failed to generate reply');
    } finally {
      setReplyLoading(false);
    }
  };

  const handleCopyReply = () => {
    navigator.clipboard.writeText(reply);
    setCopied(true);
    toast.success('Reply copied to clipboard!');
    redditPixel.replyCopied(leadId);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.updateLeadStatus(leadId, newStatus);
      setLead(prev => ({ ...prev, status: newStatus }));
      toast.success(`Marked as ${newStatus}`);
      redditPixel.leadStatusChanged(leadId, newStatus);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-4 max-w-5xl">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-48 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (!lead) return null;

  return (
    <DashboardLayout>
      {/* Back button */}
      <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}>
        <Button
          variant="ghost"
          size="sm"
          className="mb-5 text-[#5B667A] hover:text-[#0B0F17] hover:bg-[#F6F7F9]"
          onClick={() => navigate('/dashboard')}
          data-testid="lead-detail-back"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Leads
        </Button>
      </motion.div>

      <div className="grid lg:grid-cols-[1fr_420px] gap-6">
        {/* Left - Post Details */}
        <div className="space-y-4">
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
            <Card className="p-6 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
              {/* Header */}
              <div className="flex items-start justify-between gap-4 mb-5">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2.5">
                    <span className="text-xs text-[#5B667A] bg-[#F6F7F9] px-2.5 py-1 rounded-full font-medium">r/{lead.subreddit}</span>
                    {lead.author && <span className="text-xs text-[#9ca3af]">by {lead.author}</span>}
                  </div>
                  <h1 className="text-xl font-bold text-[#0B0F17] leading-snug" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                    {lead.title}
                  </h1>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#5B667A] font-medium">Intent Score:</span>
                    <IntentBadge score={lead.intent_score} />
                  </div>
                  <StatusBadge status={lead.status} />
                </div>
              </div>

              {/* Post content */}
              <div className="p-5 bg-gradient-to-br from-[#F9FAFB] to-[#F6F7F9] rounded-xl border border-[#E6E8EE]/50">
                <p className="text-sm text-[#0B0F17] leading-relaxed whitespace-pre-wrap">
                  {cleanPostBody(lead.body) || 'No content available for this post.'}
                </p>
              </div>

              {/* Matched keywords */}
              {lead.matched_keywords?.length > 0 && (
                <div className="mt-5">
                  <span className="text-xs font-semibold text-[#5B667A] block mb-2.5 uppercase tracking-wider">Matched Keywords</span>
                  <div className="flex flex-wrap gap-2">
                    {lead.matched_keywords.map((kw, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3 + i * 0.05 }}
                      >
                        <Badge variant="secondary" className="text-xs bg-[#FFF2EA] text-[#FF4500] font-medium border border-[#FF4500]/10">{kw}</Badge>
                      </motion.span>
                    ))}
                  </div>
                </div>
              )}

              {/* Reasons */}
              {lead.reasons?.length > 0 && (
                <div className="mt-5">
                  <span className="text-xs font-semibold text-[#5B667A] block mb-2.5 uppercase tracking-wider">Why this is a lead</span>
                  <div className="space-y-2">
                    {lead.reasons.map((r, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 + i * 0.1 }}
                        className="flex items-start gap-2.5 p-3 rounded-lg bg-[#FAFAFA] border border-[#E6E8EE]/50"
                      >
                        <Zap className="w-3.5 h-3.5 text-[#FF4500] mt-0.5 flex-shrink-0" />
                        <span className="text-xs text-[#374151] leading-relaxed">{r}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* View on Reddit */}
              {lead.url && (
                <div className="mt-6">
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-lg text-sm hover:bg-[#FFF2EA] hover:text-[#FF4500] hover:border-[#FF4500]/20 transition-colors"
                    onClick={() => window.open(lead.url, '_blank')}
                    data-testid="lead-detail-view-reddit"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View on Reddit
                  </Button>
                </div>
              )}
            </Card>
          </motion.div>

          {/* Status Controls */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={1}>
            <Card className="p-5 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_1px_3px_rgba(16,24,40,0.04)]">
              <span className="text-xs font-semibold text-[#5B667A] block mb-3 uppercase tracking-wider">Lead Status</span>
              <div className="flex flex-wrap gap-2" data-testid="lead-detail-status-control">
                {[
                  { status: 'new', icon: null, label: 'New' },
                  { status: 'replied', icon: MessageSquare, label: 'Replied' },
                  { status: 'saved', icon: Bookmark, label: 'Saved' },
                  { status: 'ignored', icon: X, label: 'Ignored' },
                ].map(({ status, icon: Icon, label }) => (
                  <Button
                    key={status}
                    variant={lead.status === status ? 'default' : 'outline'}
                    size="sm"
                    className={`rounded-lg text-xs font-medium transition-all ${
                      lead.status === status
                        ? 'bg-[#FF4500] hover:bg-[#e63e00] text-white shadow-sm'
                        : 'hover:bg-[#FFF2EA] hover:text-[#FF4500] hover:border-[#FF4500]/20'
                    }`}
                    onClick={() => handleStatusChange(status)}
                  >
                    {Icon && <Icon className="w-3.5 h-3.5 mr-1" />}
                    {label}
                  </Button>
                ))}
              </div>
            </Card>
          </motion.div>
        </div>

        {/* Right - AI Reply Generator */}
        <motion.div
          variants={fadeUp} initial="hidden" animate="visible" custom={2}
          className="lg:sticky lg:top-4 lg:self-start"
        >
          <Card className="p-5 rounded-2xl border-[#E6E8EE] bg-white shadow-[0_2px_8px_rgba(16,24,40,0.06),0_16px_40px_rgba(16,24,40,0.04)] overflow-hidden relative">
            {/* Decorative top bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FF4500] via-[#FF6A2A] to-[#FFB020]" />
            
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF6A2A, #FF4500)' }}>
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <h3 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>AI Reply Generator</h3>
            </div>

            {isFreePlan ? (
              /* ── Free plan: blurred + upgrade CTA ── */
              <div className="relative">
                <div className="blur-[3px] pointer-events-none select-none opacity-50">
                  <div className="mb-4">
                    <div className="flex gap-2">
                      {['Helpful', 'Direct', 'Casual'].map(t => (
                        <span key={t} className="text-xs px-4 py-1.5 rounded-full bg-[#F6F7F9] text-[#5B667A]">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="h-10 rounded-xl bg-gradient-to-r from-[#FF6A2A] to-[#FF4500] mb-4" />
                  <div className="p-6 rounded-xl bg-[#F6F7F9]">
                    <div className="h-3 w-3/4 bg-[#E6E8EE] rounded mb-2" />
                    <div className="h-3 w-full bg-[#E6E8EE] rounded mb-2" />
                    <div className="h-3 w-2/3 bg-[#E6E8EE] rounded" />
                  </div>
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center bg-white/90 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-[#E6E8EE] max-w-[260px]">
                    <div className="w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center bg-gradient-to-br from-[#FF6A2A] to-[#FF4500]">
                      <Lock className="w-4 h-4 text-white" />
                    </div>
                    <p className="text-sm font-bold text-[#0B0F17] mb-1" style={{ fontFamily: "'Space Grotesk'" }}>Pro Feature</p>
                    <p className="text-xs text-[#5B667A] mb-4">Upgrade to generate AI-powered reply drafts for every lead.</p>
                    <Button className="w-full h-9 rounded-lg text-xs font-bold cta-btn shadow-md shadow-[#FF4500]/20"
                      style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}
                      onClick={() => navigate('/settings')}>
                      Upgrade to Pro
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              /* ── Paid plan: full AI Reply Generator ── */
              <>
                <div className="mb-5">
                  <span className="text-xs font-semibold text-[#5B667A] block mb-2.5 uppercase tracking-wider">Tone</span>
                  <Tabs value={tone} onValueChange={setTone}>
                    <TabsList className="w-full bg-[#F6F7F9]">
                      <TabsTrigger value="helpful" className="flex-1 text-xs font-medium">Helpful</TabsTrigger>
                      <TabsTrigger value="direct" className="flex-1 text-xs font-medium">Direct</TabsTrigger>
                      <TabsTrigger value="casual" className="flex-1 text-xs font-medium">Casual</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>

                <Button
                  className="w-full h-11 rounded-xl font-bold text-sm cta-btn shadow-md mb-5"
                  style={{ background: 'linear-gradient(135deg, #FF6A2A 0%, #FF4500 100%)' }}
                  onClick={handleGenerateReply}
                  disabled={replyLoading}
                  data-testid="lead-detail-generate-reply-button"
                >
                  {replyLoading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Generate Reply</>
                  )}
                </Button>

                {reply ? (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="lead-detail-generated-reply">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-[#5B667A] uppercase tracking-wider">Generated Reply</span>
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleCopyReply} data-testid="lead-detail-copy-reply-button">
                        {copied ? <Check className="w-3 h-3 mr-1 text-[#16A34A]" /> : <Copy className="w-3 h-3 mr-1" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                    <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={10}
                      className="text-sm leading-relaxed resize-none rounded-xl border-[#E6E8EE] bg-[#FAFAFA] focus:bg-white transition-colors" />
                    <Button variant="outline" size="sm"
                      className="mt-3 rounded-lg text-xs hover:bg-[#FFF2EA] hover:text-[#FF4500] hover:border-[#FF4500]/20"
                      onClick={handleGenerateReply} disabled={replyLoading}>
                      <RefreshCw className={`w-3 h-3 mr-1 ${replyLoading ? 'animate-spin' : ''}`} /> Regenerate
                    </Button>
                  </motion.div>
                ) : (
                  <div className="p-8 rounded-xl bg-gradient-to-br from-[#FAFAFA] to-[#F6F7F9] border border-[#E6E8EE]/50 text-center">
                    <div className="w-12 h-12 rounded-2xl mx-auto mb-3 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FFF2EA, #FFE8DE)' }}>
                      <MessageSquare className="w-5 h-5 text-[#FF4500]" />
                    </div>
                    <p className="text-sm text-[#5B667A] font-medium">Click "Generate Reply" to create a draft response for this thread.</p>
                  </div>
                )}
              </>
            )}
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
